import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Radio, Instagram, Facebook } from "lucide-react";
import { ASSETS, SOCIALS } from "@/lib/assets";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Roster", to: "/roster" },
  { label: "KSSR Radio", to: "/radio", radio: true },
  { label: "Submissions & Contact", to: "/submissions" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="global-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-charcoal/80 backdrop-blur-xl border-b border-slate/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-4 md:px-12 lg:px-16">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
            <img
              src={ASSETS.labelLogo}
              alt="Sorrow & Serenity Music"
              className="h-14 w-14 object-contain transition-transform duration-500 group-hover:scale-105"
            />
            <span className="hidden sm:block font-label uppercase tracking-[0.25em] text-xs text-softwhite/80 leading-tight">
              Sorrow &amp; Serenity
              <span className="block text-slate">Music</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`nav-link font-label uppercase tracking-[0.18em] text-[13px] transition-colors duration-300 flex items-center gap-2 ${
                    item.radio
                      ? "text-crimson-bright"
                      : active
                      ? "text-softwhite"
                      : "text-slate hover:text-softwhite"
                  }`}
                  style={item.radio ? { textShadow: "0 0 12px rgba(163,0,0,0.7)" } : {}}
                >
                  {item.radio && <Radio size={14} className="animate-pulse-glow" />}
                  {item.label}
                </Link>
              );
            })}
            <div className="flex items-center gap-4 pl-2 border-l border-slate/20">
              <a
                href={SOCIALS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-instagram"
                aria-label="Instagram"
                className="text-slate hover:text-crimson-bright transition-colors duration-300"
              >
                <Instagram size={18} />
              </a>
              <a
                href={SOCIALS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-facebook"
                aria-label="Facebook"
                className="text-slate hover:text-crimson-bright transition-colors duration-300"
              >
                <Facebook size={18} />
              </a>
            </div>
          </nav>

          {/* Mobile toggle */}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-softwhite p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-charcoal/95 backdrop-blur-xl border-b border-slate/20"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`font-label uppercase tracking-[0.18em] text-base ${
                    item.radio ? "text-crimson-bright" : "text-softwhite"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <a
                href={SOCIALS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-instagram"
                className="flex items-center gap-3 font-label uppercase tracking-[0.18em] text-base text-slate"
              >
                <Instagram size={18} /> Instagram
              </a>
              <a
                href={SOCIALS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-facebook"
                className="flex items-center gap-3 font-label uppercase tracking-[0.18em] text-base text-slate"
              >
                <Facebook size={18} /> Facebook
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
